import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import head from "lodash/head";
import get from "lodash/get";
import size from "lodash/size";
import filter from "lodash/filter";
import reduce from "lodash/reduce";
import sortBy from "lodash/sortBy";
import toLower from "lodash/toLower";
import uniqBy from "lodash/uniqBy";
import { useForm } from "react-hook-form";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiButton from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import Button from "../shared/Button";
import EstateService from "../../services/EstateService";
import EstateAssociationItem from "./EstateAssociationItem";
import { useFileContext } from "../context/FileWrapper";
import { getStaticDocTypes } from "../../helpers/utils";
import SelectController from "../forms/SelectController";
import TextFieldController from "../forms/TextFieldController";
import Grid from "@mui/material/Grid2";
import {
  ESTATE_DISPLAY_STATUS,
  PURPOSE_STATUS,
  PURPOSES,
} from "../../helpers/constants";
import CheckboxController from "../forms/CheckboxController";
import AutocompleteController from "../forms/AutocompleteController";
import useOffices from "../hooks/useOffices";

const DOCUMENT_TYPES = getStaticDocTypes();

const EstateAssociation = ({ uuid, data, handleBack, closeModal, file }) => {
  const { filesAssociation, updateFileInformation, addAssociation } =
    useFileContext();

  const { control, watch, getValues } = useForm({
    defaultValues: {
      searchBy: "id",
      value: "",
      purpose: "",
      purposeStatus: [],
      displayStatus: [],
      offices: [],
      isTopParent: true,
    },
  });

  const [selectedEstates, setSelectedEstates] = useState(
    filesAssociation[uuid] || []
  );

  const { data: matching } = useQuery({
    queryKey: ["estates-mapping", data.name, ...data.offices],
    queryFn: () =>
      EstateService.getEstatesSuggestions({
        filenames: [data.name],
        officeIds: data.offices,
      }),
  });

  const { data: result, refetch } = useQuery({
    enabled: false,
    queryFn: async () => {
      let data = {};
      if (getValues("searchBy") !== "id") {
        data = {
          purpose: getValues("purpose"),
          purposeStatus: getValues("purposeStatus"),
          displayStatus: getValues("displayStatus"),
          offices: getValues("offices"),
          isTopParent: getValues("isTopParent"),
        };
      }
      return await EstateService.getEstates({
        search: {
          key: getValues("searchBy"),
          value: getValues("value"),
        },
        canHaveChildren: true,
        ...data,
      });
    },
  });

  const handleSelectEstate = (estate) => {
    const { id: estateId } = estate;
    const exists = selectedEstates.some(({ id }) => id === estateId);
    if (exists) {
      setSelectedEstates(selectedEstates.filter(({ id }) => id !== estateId));
    } else {
      setSelectedEstates([...selectedEstates, estate]);
    }
  };

  const onConfirm = () => {
    let updatedData = { ...data };
    const selectedDocumentType = DOCUMENT_TYPES.find(
      (docType) => docType.id === data.documentType
    );
    updatedData.documentType = get(selectedDocumentType, "id");
    updatedData.documentTypeName = get(selectedDocumentType, "name");

    updatedData.effectiveDateStr =
      !!data.effectiveDate && data.effectiveDate.isValid()
        ? data.effectiveDate.format("DD-MM-YYYY")
        : null;
    addAssociation(uuid, uniqBy(selectedEstates, "id"));
    updateFileInformation(uuid, updatedData);
    closeModal();
  };

  const fileMatching = head(matching?.data);

  useEffect(() => {
    if (size(selectedEstates) === 0 && size(fileMatching) > 0) {
      setSelectedEstates(fileMatching.estates);
    }
  }, [matching]);

  return (
    <StyleWrapper px={3} py={4} gap={3}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Typography fontWeight={700} fontSize={26} color="var(--text-color)">
          Biens suggérés
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          flexShrink={0}
          gap={2}
          sx={{
            border: "1px solid #1118270D",
            borderRadius: "6px",
            height: "52px",
            padding: "15px 20px",
            maxWidth: "500px",
          }}
        >
          <img
            className="upload-file-icon"
            src="/assets/icons/file-item.svg"
            alt=""
          />
          <Typography
            className="text-ellipsis"
            fontSize={14}
            fontWeight={500}
            color="var(--text-color)"
          >
            {file.content.name}
          </Typography>
        </Box>
      </Stack>
      <Stack gap={2} px={1} sx={{ maxHeight: "330px", overflowY: "auto" }}>
        {fileMatching?.estates?.map((estate) => (
          <EstateAssociationItem
            key={estate.id}
            estate={estate}
            handleSelect={handleSelectEstate}
            isSelected={selectedEstates.some(({ id }) => id === estate.id)}
          />
        ))}
      </Stack>
      <Stack
        className="search-bar"
        sx={{
          width: "1100px",
          backgroundColor: "var(--red-color)",
          borderRadius: "6px",
        }}
        p={4}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ minHeight: "108px" }}
          gap={4}
        >
          <Box flexShrink={0}>
            <Typography fontWeight={700} fontSize={26} color="var(--white)">
              Recherchez d'autres biens
            </Typography>
            <Typography fontWeight={400} fontSize={14} color="var(--white)">
              Lorem ipsum dolor sit amet consectetur. Libero purus est sed ut.
            </Typography>
          </Box>
          <Filter
            control={control}
            getValues={getValues}
            watch={watch}
            handleSearch={refetch}
          />
        </Stack>
        <Stack
          gap={2}
          sx={{ maxHeight: 180, px: 1, overflowY: "auto", overflowX: "hidden" }}
        >
          {result?.estates?.map((estate) => (
            <EstateAssociationItem
              key={estate.id}
              estate={estate}
              handleSelect={handleSelectEstate}
              isSelected={selectedEstates.some(({ id }) => id === estate.id)}
            />
          ))}
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="center" gap={2}>
        <Button onClick={handleBack}>Retour</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>
          Confirmer
        </Button>
      </Stack>
    </StyleWrapper>
  );
};

const Filter = ({ control, getValues, watch, handleSearch }) => {
  const [displayMoreFilter, setDisplayMoreFilter] = useState(false);

  const { data: officesData } = useOffices();

  const estateDisplaysStatusById = useMemo(
    () =>
      reduce(
        ESTATE_DISPLAY_STATUS,
        (acc, curr) => {
          if (!acc[curr.id]) {
            acc[curr.id] = curr;
          }
          return acc;
        },
        {}
      ),
    []
  );

  const { purposeStatusOption, purposeStatusById } = useMemo(() => {
    const purpose = getValues("purpose");
    const purposeStatusOption = filter(
      PURPOSE_STATUS,
      (ps) => ps.purposeId === purpose
    );
    const purposeStatusById = reduce(
      purposeStatusOption,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      purposeStatusOption,
      purposeStatusById,
    };
  }, [watch("purpose")]);

  const { officesOptions, officesById } = useMemo(() => {
    const officesOptions = sortBy(officesData, (item) =>
      toLower(get(item, "name"))
    );
    const officesById = reduce(
      officesOptions,
      (acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = curr;
        }
        return acc;
      },
      {}
    );
    return {
      officesOptions,
      officesById,
    };
  }, [officesData]);

  const toggleMoreFilter = () => setDisplayMoreFilter(!displayMoreFilter);

  return (
    <Stack gap={2}>
      <Box display="flex" gap={2}>
        <TextFieldController
          control={control}
          size="small"
          className="search-input"
          label="Saisissez le nom ou l'ID du bien"
          name="value"
        />
        <SelectController
          control={control}
          name="searchBy"
          size="small"
          className="search-input"
          options={[
            { name: "ID", id: "id" },
            { name: "Nom", id: "keyword" },
          ]}
          placeholder="Rechercher par"
        />
        <Button
          className="search-btn"
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSearch}
        >
          Rechercher
        </Button>
      </Box>
      <MuiButton
        size="small"
        sx={{ color: "var(--white)" }}
        startIcon={displayMoreFilter ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={toggleMoreFilter}
      >
        {displayMoreFilter ? "Moins" : "Plus"} de filtre
      </MuiButton>
      {displayMoreFilter && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }} display="flex" flexDirection="column">
            <label className="form-label">Type de transaction</label>
            <SelectController
              control={control}
              name="purpose"
              options={PURPOSES}
              size="small"
              className="search-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Status de transaction</label>
            <AutocompleteController
              control={control}
              name="purposeStatus"
              options={purposeStatusOption}
              optionsById={purposeStatusById}
              size="small"
              className="search-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Visibilité</label>
            <AutocompleteController
              control={control}
              name="displayStatus"
              options={ESTATE_DISPLAY_STATUS}
              optionsById={estateDisplaysStatusById}
              size="small"
              className="search-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Bureau</label>
            <AutocompleteController
              control={control}
              name="offices"
              options={officesOptions}
              optionsById={officesById}
              size="small"
              className="search-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CheckboxController
              name="isTopParent"
              control={control}
              label="Afficher uniquement les projets"
              className="search-input"
              sx={{
                "&.Mui-checked": {
                  color: "var(--white)",
                },
              }}
            />
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};

const StyleWrapper = styled(Stack)`
  button.search-btn {
    background-color: var(--white) !important;
    color: var(--red-color);
    border: none;
    font-size: 13px;
    font-weight: 400;
    width: 144px;
  }

  .search-input {
    input {
      color: var(--white);
    }

    label {
      color: var(--white);

      &.Mui-focused {
        color: var(--white);
      }
    }

    &.MuiSelect-root {
      width: auto;
    }

    .MuiSelect-icon,
    .MuiAutocomplete-endAdornment svg {
      color: var(--white);
    }

    & .MuiOutlinedInput-root,
    &.MuiOutlinedInput-root {
      color: var(--white);
      & fieldset {
        border-color: var(--white);
      }
      &:hover fieldset {
        border-color: var(--white);
      }
      &.Mui-focused fieldset {
        border-color: var(--white);
      }
      &.Mui-focused label {
        color: var(--white);
      }
    }
  }

  .search-bar .estate__list__item {
    width: 100%;
    box-shadow: none;
  }

  label.form-label {
    font-weight: 500;
    color: var(--white);
  }

  .MuiFormControlLabel-label {
    color: var(--white);
  }

  .MuiCheckbox-root svg {
    fill: var(--white);
  }
`;

export default EstateAssociation;
